import React from 'react';
import {
  SimpleForm,
  SelectInput,
  TextInput,
  NumberInput,
  number,
  minValue,
  required,
  useGetList,
  useInput,
} from 'react-admin';

import type { FacetGroup } from '@src/facets';

export const SOURCE_COLUMNS = [
  'price',
  'msrp_price',
  'last_known_retail_price',
  'last_known_outlet_price',
  'absolute_price',
] as const;

const SOURCE_COLUMN_CHOICES = SOURCE_COLUMNS.map((col) => ({ id: col, name: col }));

interface PricingRulesFormProps {
  toolbar?: React.ReactElement | false;
}

/**
 * Form fields for primary name that depend on the selected primary tag.
 */
const PrimaryNameInput = () => {
  const { field: tagField } = useInput({ source: 'tag' });
  const selectedTag = tagField.value as string;
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const tagFacet = facetGroups.find(fg => fg.tag === selectedTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="name"
      label="Primary Name"
      choices={nameChoices}
      disabled={!selectedTag || nameChoices.length === 0}
      fullWidth
      resettable
      helperText={!selectedTag ? 'Select a primary tag first' : undefined}
    />
  );
};

/**
 * Form fields for secondary name that depend on the selected secondary tag.
 */
const SecondaryNameInput = () => {
  const { field: secondaryTagField } = useInput({ source: 'secondaryTag' });
  const selectedSecondaryTag = secondaryTagField.value as string;
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const tagFacet = facetGroups.find(fg => fg.tag === selectedSecondaryTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="secondaryName"
      label="Secondary Name"
      choices={nameChoices}
      disabled={!selectedSecondaryTag || nameChoices.length === 0}
      fullWidth
      resettable
      helperText={!selectedSecondaryTag ? 'Select a secondary tag first' : undefined}
    />
  );
};

/**
 * Form fields for tertiary name that depend on the selected tertiary tag.
 */
const TertiaryNameInput = () => {
  const { field: tertiaryTagField } = useInput({ source: 'tertiaryTag' });
  const selectedTertiaryTag = tertiaryTagField.value as string;
  const { data: facetGroups = [] } = useGetList<FacetGroup>('facets');

  const tagFacet = facetGroups.find(fg => fg.tag === selectedTertiaryTag);
  const nameChoices = tagFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  return (
    <SelectInput
      source="tertiaryName"
      label="Tertiary Name"
      choices={nameChoices}
      disabled={!selectedTertiaryTag || nameChoices.length === 0}
      fullWidth
      resettable
      helperText={!selectedTertiaryTag ? 'Select a tertiary tag first' : undefined}
    />
  );
};

const PricingRulesForm = ({ toolbar }: PricingRulesFormProps) => {
  const { data: facetGroups = [], isLoading } = useGetList<FacetGroup>('facets');

  const departmentFacet = facetGroups.find(fg => fg.tag === 'department');
  const categoryFacet = facetGroups.find(fg => fg.tag === 'category');

  const departmentChoices = departmentFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  const categoryChoices = categoryFacet?.values.map(v => ({
    id: v.name,
    name: v.name,
  })) || [];

  const tagChoices = facetGroups.map(fg => ({
    id: fg.tag,
    name: fg.tag,
  }));

  return (
    <SimpleForm toolbar={toolbar}>
      <SelectInput
        source="sourceColumn"
        label="Source Column"
        choices={SOURCE_COLUMN_CHOICES}
        validate={required()}
        defaultValue="price"
        fullWidth
      />
      <NumberInput
        source="discount"
        label="Discount (% or cents for absolute_price)"
        validate={[required(), number(), minValue(0)]}
        fullWidth
        helperText="For percentage: 0-100. For absolute_price: amount in cents."
      />
      <SelectInput
        source="department"
        label="Department"
        choices={departmentChoices}
        disabled={isLoading}
        fullWidth
        resettable
        helperText="Leave empty for all departments"
      />
      <SelectInput
        source="category"
        label="Category"
        choices={categoryChoices}
        disabled={isLoading}
        fullWidth
        resettable
        helperText="Leave empty for all categories"
      />
      <TextInput
        source="merchantCategory"
        label="Merchant Category"
        fullWidth
        helperText="Leave empty for all merchant categories"
      />
      <TextInput
        source="upc"
        label="UPC"
        fullWidth
        helperText="Leave empty for all UPCs"
      />
      <SelectInput
        source="tag"
        label="Primary Tag"
        choices={tagChoices}
        disabled={isLoading}
        fullWidth
        resettable
        helperText="Leave empty for all tags"
      />
      <PrimaryNameInput />
      <SelectInput
        source="secondaryTag"
        label="Secondary Tag"
        choices={tagChoices}
        disabled={isLoading}
        fullWidth
        resettable
        helperText="Leave empty for all tags"
      />
      <SecondaryNameInput />
      <SelectInput
        source="tertiaryTag"
        label="Tertiary Tag"
        choices={tagChoices}
        disabled={isLoading}
        fullWidth
        resettable
        helperText="Leave empty for all tags"
      />
      <TertiaryNameInput />
    </SimpleForm>
  );
};

export default PricingRulesForm;
